import React, { useState } from 'react';
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  Modal,
  Platform,
  TextInput,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

/**
 * Date range picker component
 * 
 * @param {object} props
 * @param {Date} props.startDate - Start date
 * @param {Date} props.endDate - End date
 * @param {function} props.onStartDateChange - Callback when start date changes
 * @param {function} props.onEndDateChange - Callback when end date changes
 * @param {string} props.label - Optional label for the component
 * @param {object} props.style - Additional styling for the container
 */
const DateRangePicker = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  label,
  style,
}) => {
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);
  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Handle date selection for iOS
  const handleConfirm = (selectedDate, isStartDate) => {
    if (isStartDate) {
      onStartDateChange(selectedDate);
      setShowStartPicker(false);
    } else {
      onEndDateChange(selectedDate);
      setShowEndPicker(false);
    }
  };

  // Handle date selection for Android
  const onChange = (event, selectedDate, isStartDate) => {
    if (Platform.OS === 'android') {
      if (event.type === 'dismissed') {
        isStartDate ? setShowStartPicker(false) : setShowEndPicker(false);
        return;
      }
      
      if (isStartDate) {
        setShowStartPicker(false);
        if (selectedDate) onStartDateChange(selectedDate);
      } else {
        setShowEndPicker(false);
        if (selectedDate) onEndDateChange(selectedDate);
      }
    } else {
      // On iOS, we don't hide the picker on selection
      if (isStartDate && selectedDate) {
        onStartDateChange(selectedDate);
      } else if (!isStartDate && selectedDate) {
        onEndDateChange(selectedDate);
      }
    }
  };

  // Helper function to format date without timezone issues
  const formatDateForInput = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Initialize input values when dates change
  React.useEffect(() => {
    setStartDateInput(formatDateForInput(startDate));
  }, [startDate]);

  React.useEffect(() => {
    setEndDateInput(formatDateForInput(endDate));
  }, [endDate]);

  const formatDateInput = (value) => {
    // Remove non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as YYYY-MM-DD
    if (digits.length >= 8) {
      return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
    } else if (digits.length >= 6) {
      return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
    } else if (digits.length >= 4) {
      return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    }
    return digits;
  };

  const handleDateInputChange = (value, isStartDate) => {
  if (isStartDate) {
    setStartDateInput(value);
  } else {
    setEndDateInput(value);
  }

  if (value.length === 10 && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = value.split('-').map(Number);
    const selectedDate = new Date(year, month - 1, day);
    if (!isNaN(selectedDate.getTime())) {
      if (isStartDate) {
        onStartDateChange(selectedDate);
      } else {
        onEndDateChange(selectedDate);
      }
    }
  }
};

  const renderDatePicker = (isStartDate) => {
    const show = isStartDate ? showStartPicker : showEndPicker;
    const inputValue = isStartDate ? startDateInput : endDateInput;

  const isValidDate = (dateStr) =>
  /^\d{4}-\d{2}-\d{2}$/.test(dateStr) &&
  !isNaN(new Date(dateStr).getTime());  

    if (!show) return null;

    return (
      <Modal
        transparent={true}
        animationType="slide"
        visible={show}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <View style={styles.pickerHeader}>
              <Text style={styles.pickerTitle}>
                {isStartDate ? 'Select Start Date' : 'Select End Date'}
              </Text>
              <TouchableOpacity
                onPress={() => isStartDate ? setShowStartPicker(false) : setShowEndPicker(false)}
              >
                <Ionicons name="close" size={24} color="#7f8c8d" />
              </TouchableOpacity>
            </View>

            <Text style={styles.dateInputLabel}>Enter date:</Text>
            <TextInput
              style={[
              styles.dateInput,
              !isValidDate(startDateInput) && startDateInput.length === 10 && { borderColor: 'red' },
              ]}
              value={inputValue}
              onChangeText={(value) => handleDateInputChange(value, isStartDate)}
              placeholder="YYYY-MM-DD"
              maxLength={10}
              keyboardType="default"
            />

            <TouchableOpacity
              style={styles.confirmButton}
              onPress={() => isStartDate ? setShowStartPicker(false) : setShowEndPicker(false)}
            >
              <Text style={styles.confirmButtonText}>Done</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    );
  };

  return (
    <View style={[styles.container, style]}>
      {label && <Text style={styles.label}>{label}</Text>}
      
      <View style={styles.dateContainer}>
        <TouchableOpacity
          style={styles.dateButton}
          onPress={() => setShowStartPicker(true)}
        >
          <Ionicons name="calendar-outline" size={16} color="#3498db" style={styles.icon} />
          <Text style={styles.dateText}>
            {startDate ? formatDate(startDate) : 'Start Date'}
          </Text>
        </TouchableOpacity>

        <View style={styles.separator}>
          <Text style={styles.separatorText}>to</Text>
        </View>

        <TouchableOpacity
          style={styles.dateButton}
          onPress={() => setShowEndPicker(true)}
        >
          <Ionicons name="calendar-outline" size={16} color="#3498db" style={styles.icon} />
          <Text style={styles.dateText}>
            {endDate ? formatDate(endDate) : 'End Date'}
          </Text>
        </TouchableOpacity>
      </View>

      {renderDatePicker(true)}
      {renderDatePicker(false)}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  label: {
    fontSize: 16,
    fontWeight: '500',
    color: '#2c3e50',
    marginBottom: 8,
  },
  dateContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dateButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  icon: {
    marginRight: 8,
  },
  dateText: {
    fontSize: 14,
    color: '#2c3e50',
  },
  separator: {
    paddingHorizontal: 8,
  },
  separatorText: {
    color: '#7f8c8d',
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    padding: 16,
  },
  pickerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  pickerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  iOSPicker: {
    height: 200,
  },
  confirmButton: {
    backgroundColor: '#3498db',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    marginTop: 16,
  },
  confirmButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  webDateInput: {
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#f9f9f9',
    marginBottom: 16,
  },
  dateInputLabel: {
    fontSize: 14,
    color: '#7f8c8d',
    marginBottom: 8,
  },
  dateInput: {
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#f9f9f9',
    marginBottom: 16,
  },
});

export default DateRangePicker;